import { Injectable } from "@angular/core";
import { API_ENDPOINT } from "./endpoints";
import { ApiService } from ".";
import { tERPConnectionRequest } from "app/types/erp-config.type";
import { ErpType } from "app/shared/enums";
import { ERPConfigModel } from "app/models/erp";
import { Observable } from "rxjs";
import { ResponseModel } from "app/models/shared";

@Injectable({ providedIn: 'root' })
export class ERPIntegrationService {
    constructor(private apiService: ApiService) {

    }

    getERPConfigs() {
        return this.apiService.get<ERPConfigModel[]>(API_ENDPOINT.Erp);
    };

    getERPConfig(type: ErpType) {
        return this.apiService.get<ERPConfigModel>(`${API_ENDPOINT.Erp}/${type}`);
    };

    upsertERPConfig(body: tERPConnectionRequest) {          
        return this.apiService.post(`${API_ENDPOINT.Erp}`, body);
    };

    uploadERPData(type: ErpType, file: File) {          
        const formData = new FormData();
        formData.append('file', file);

        // skipcq: JS-0323
        return this.apiService.postFormData<any>(`${API_ENDPOINT.Erp}/${type}/upload`, formData);
    };

    connectERPConnection(type: ErpType, body: tERPConnectionRequest) {
        return this.apiService.post(`${API_ENDPOINT.Erp}/connect/${type}`, body);
    };

    connectAcumatica(body: any) {
        // skipcq: JS-0323
        return this.apiService.post(`${API_ENDPOINT.Erp}/acumatica/connect`, body);
    };

    disconnectERPConnection(type: string, organizationId?: string) {
        return this.apiService.get(`${API_ENDPOINT.Erp}/disconnect/${type}?organization_id=${organizationId ?? ''}`);
    };

    setDisconnectERPConnection(type: string) {
        return this.apiService.get(`${API_ENDPOINT.Erp}/set-disconnect/${type}`);
    };

    getViewpointAppVersion() {
        return this.apiService.get(`${API_ENDPOINT.Erp}/viewpoint/app-version`);
    };

    checkERPConfigurated() {
        return this.apiService.get(`${API_ENDPOINT.Erp}/has-configurated`);
    }

    checkERPsInterrupted() {
        return this.apiService.get<ERPConfigModel[]>(`${API_ENDPOINT.Erp}/has-interrupted`);
    }

    getOAuthUrl(type: ErpType) {
        return this.apiService.get<{ authorization_url: string; state: string; }>(`${API_ENDPOINT.Erp}/oauth/${type}/url`);
    }

    removeOrganization(type: ErpType, id: string): Observable<ResponseModel<{is_clear_erp_config: boolean}>> {
        return this.apiService.delete<{is_clear_erp_config: boolean}>(`${API_ENDPOINT.Erp}/${type}/organization/${id}`);
    }

    retryExtraction(type: ErpType, organizationId: string): Observable<ResponseModel> {
        return this.apiService.post(`${API_ENDPOINT.Erp}/${type}/organization/${organizationId}/retry`, {});
    }

    requestFormatSupport(importId: string, note?: string): Observable<ResponseModel<{ status: string; alreadyRequested: boolean; requestedAt: string }>> {
        return this.apiService.post<{ status: string; alreadyRequested: boolean; requestedAt: string }>(`api/ap-transactions/${importId}/request-format-support`, { note: note ?? '' });
    }

    claimViewpointPairing(code: string): Observable<any> {
        return this.apiService.post(`${API_ENDPOINT.Erp}/viewpoint/claim-pairing`, { code });
    }

    /**
     * Trigger on-demand AP data pull via Tailscale
     * Returns success if Tailscale is available and pull was successful,
     * or fallback info if Tailscale is not available
     */
    pullApDataOnDemand(params: {
        config_id: string;
        statement_id: string;
        vendor_id: string;
        tenant_id: string;  // Viewpoint APCo
        company_id?: number; // Internal company ID (optional)
        start_date?: Date;
        end_date?: Date;
    }): Observable<ResponseModel<{ success?: boolean; fallback?: string; tailscale_enabled?: boolean; async_processing?: boolean; records_processed?: number }>> {
        return this.apiService.post<{ success?: boolean; fallback?: string; tailscale_enabled?: boolean; async_processing?: boolean; records_processed?: number }>(
            `${API_ENDPOINT.Erp}/viewpoint/pull-on-demand`,
            params
        );
    }
}
